// ============================================================================
// Direct mode — a sectioned note edited as plain text (no questionnaire)
// ============================================================================
// Many firms keep their own wording for the Accounting Policies. In Direct
// mode (workspace `authoring_mode: 'DIRECT'`) every section holds its own
// text; the questionnaire no longer picks, titles or leaves out sections
// (the print mirrors this — SectionedNarrativeResolver.IsDirect). A note
// becomes Direct from:
//   · its own current text   — materialiseDirect (nothing lost)
//   · another engagement     — portable* + applyPortable (text COPIED, never
//                               linked; figures re-fill from this engagement)
//   · blank                  — startBlank
// and goes back with switchToGuided (its text kept as "Previous version text").

import type {
  ReportAddedSection,
  ReportSectionDefinition,
  ReportSectionOverride,
  ReportWorkspaceData,
} from 'api/types/fsa/reportData.types';
import type { CorporateFactsData } from 'api/types/fsa/corporateFacts.types';
import type { NoteAddedPolicySection, PolicySectionEntry } from 'api/types/fsa/noteData.types';
import { isOmitted, resolveVariant } from './reportSections';

export const DIRECT = 'DIRECT' as const;

export const isDirect = (ws: ReportWorkspaceData | null | undefined) => ws?.authoring_mode === DIRECT;

/** One section of a note, detached from any component — what a copy carries. */
export interface PortableSection {
  /** The component section code, when the section is a template section. */
  code?: string;
  title: string;
  content: unknown | null;
  hidden: boolean;
}

/** Where a copy came from — shown in the "review and Save" banner. */
export interface CopiedFrom {
  engagement_id: string;
  engagement_code?: string | null;
  client_name?: string | null;
  financial_year_end?: string | null;
}

const EMPTY_DOC = { type: 'doc', content: [{ type: 'paragraph' }] };

/** A new section id — same form as SectionedNarrativeEditor's (kept here so this module stays editor-free and testable). */
function generateLocalId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return 'sec-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

/** Stable order: listed ids first (as listed), then the rest in their natural order — mirrors the print. */
function ordered<T extends { id: string }>(items: T[], order: string[] | undefined): T[] {
  if (!order?.length) return items;
  const index = new Map(order.map((id, i) => [id, i] as const));
  return items
    .map((item, pos) => ({ item, pos }))
    .sort((a, b) => (index.get(a.item.id) ?? Number.MAX_SAFE_INTEGER) - (index.get(b.item.id) ?? Number.MAX_SAFE_INTEGER) || a.pos - b.pos)
    .map(x => x.item);
}

/** Without the keys that belong to the other mode / the previous copy. */
function base(ws: ReportWorkspaceData): ReportWorkspaceData {
  const next = { ...ws };
  delete next.carried_from_py;
  delete next.copied_from;
  return next;
}

/**
 * Guided → Direct keeping exactly what the note says now: each section takes
 * the text and title in force (answers applied); a section the questionnaire
 * leaves out becomes hidden; per-section answer overrides are dropped.
 */
export function materialiseDirect(
  defs: ReportSectionDefinition[],
  ws: ReportWorkspaceData,
  facts: CorporateFactsData | undefined,
): ReportWorkspaceData {
  const data: Record<string, ReportSectionOverride> = { ...(ws.section_data ?? {}) };
  for (const d of defs) {
    const cur: ReportSectionOverride = { ...(data[d.code] ?? {}) };
    const r = resolveVariant(d, cur, facts);
    delete cur.variant_override;
    if (isOmitted(d, r)) cur.is_hidden = true;
    if (cur.content == null) cur.content = r.content ?? EMPTY_DOC;
    if (!cur.custom_title && r.title && r.title !== d.title) cur.custom_title = r.title;
    data[d.code] = cur;
  }
  return { ...base(ws), section_data: data, authoring_mode: DIRECT };
}

/** A sectioned (generated) note's sections as they read — for copying. */
export function portableFromSectioned(
  defs: ReportSectionDefinition[],
  ws: ReportWorkspaceData,
  facts: CorporateFactsData | undefined,
): PortableSection[] {
  const data = ws.section_data ?? {};
  const direct = isDirect(ws);
  const items: Array<PortableSection & { id: string }> = [];
  for (const d of defs) {
    const ov = data[d.code] ?? {};
    const r = resolveVariant(d, ov, facts);
    items.push({
      id: d.code,
      code: d.code,
      title: ov.custom_title ?? (direct ? null : r.title) ?? d.title,
      content: ov.content ?? r.content ?? null,
      hidden: !!ov.is_hidden || (!direct && isOmitted(d, r)),
    });
  }
  for (const a of ws.added_sections ?? []) {
    const ov = data[a.local_id] ?? {};
    items.push({ id: a.local_id, title: ov.custom_title ?? a.title, content: ov.content ?? a.content ?? null, hidden: !!ov.is_hidden });
  }
  return ordered(items, ws.section_order).map(({ id: _id, ...p }) => p);
}

/** A note still on the old free-edit format — template sections from its column spec. */
export function portableFromLegacy(
  columnSpecJson: string | null | undefined,
  ws: {
    policy_section_data?: Record<string, PolicySectionEntry>;
    added_policy_sections?: NoteAddedPolicySection[];
    policy_section_order?: string[];
  },
): PortableSection[] {
  let spec: Array<{ code: string; title?: string; default_content?: unknown }> = [];
  try {
    const arr = JSON.parse(columnSpecJson ?? '[]');
    if (Array.isArray(arr)) spec = arr.filter((s: any) => s && typeof s.code === 'string');
  } catch { /* none */ }
  const data = ws.policy_section_data ?? {};
  const items: Array<PortableSection & { id: string }> = spec.map(s => {
    const ov = (data[s.code] ?? {}) as PolicySectionEntry & { custom_title?: string; is_hidden?: boolean };
    return { id: s.code, code: s.code, title: ov.custom_title ?? s.title ?? s.code, content: ov.content ?? s.default_content ?? null, hidden: !!ov.is_hidden };
  });
  for (const a of ws.added_policy_sections ?? []) {
    const ov = (data[a.local_id] ?? {}) as PolicySectionEntry & { custom_title?: string; is_hidden?: boolean };
    items.push({ id: a.local_id, title: ov.custom_title ?? a.title, content: ov.content ?? a.content ?? null, hidden: !!ov.is_hidden });
  }
  return ordered(items, ws.policy_section_order).map(({ id: _id, ...p }) => p);
}

/**
 * The target note as a Direct copy of `portable`: a source section whose code
 * this note's version also has takes that section's place; any other source
 * section comes in as an added section; this note's sections the source does
 * not have are hidden. The source's order is kept. Field values already
 * snapshotted here stay this engagement's own.
 */
export function applyPortable(
  defs: ReportSectionDefinition[],
  ws: ReportWorkspaceData,
  portable: PortableSection[],
  copiedFrom: CopiedFrom,
): ReportWorkspaceData {
  const codes = new Set(defs.map(d => d.code));
  const data: Record<string, ReportSectionOverride> = {};
  const added: ReportAddedSection[] = [];
  const order: string[] = [];
  const used = new Set<string>();
  portable.forEach(p => {
    if (p.code && codes.has(p.code) && !used.has(p.code)) {
      used.add(p.code);
      const def = defs.find(d => d.code === p.code)!;
      const entry: ReportSectionOverride = { content: p.content ?? EMPTY_DOC };
      if (p.title && p.title !== def.title) entry.custom_title = p.title;
      if (p.hidden) entry.is_hidden = true;
      data[p.code] = entry;
      order.push(p.code);
    } else {
      const id = generateLocalId();
      added.push({ local_id: id, title: p.title || 'Section', content: p.content ?? EMPTY_DOC, display_order: added.length + 1 });
      if (p.hidden) data[id] = { is_hidden: true };
      order.push(id);
    }
  });
  for (const d of defs) {
    if (used.has(d.code)) continue;
    data[d.code] = { is_hidden: true };
    order.push(d.code);
  }
  return {
    ...base(ws),
    section_data: data,
    added_sections: added,
    section_order: order,
    authoring_mode: DIRECT,
    copied_from: copiedFrom,
  };
}

/** Direct with nothing in it: every template section hidden, no added sections. */
export function startBlank(defs: ReportSectionDefinition[], ws: ReportWorkspaceData): ReportWorkspaceData {
  const data: Record<string, ReportSectionOverride> = {};
  for (const d of defs) data[d.code] = { is_hidden: true };
  return { ...base(ws), section_data: data, added_sections: [], section_order: defs.map(d => d.code), authoring_mode: DIRECT };
}

/**
 * Direct → Guided: the questionnaire picks the wording again. The Direct text
 * of every template section is kept, read-only, as "Previous version text";
 * sections the auditor added stay as they are.
 */
export function switchToGuided(defs: ReportSectionDefinition[], ws: ReportWorkspaceData): ReportWorkspaceData {
  const data: Record<string, ReportSectionOverride> = { ...(ws.section_data ?? {}) };
  const kept = [...(ws.legacy_sections ?? [])];
  for (const d of defs) {
    const ov = data[d.code];
    if (!ov) continue;
    if (ov.content != null && !ov.is_hidden) kept.push({ code: `${d.code}#direct`, title: ov.custom_title ?? d.title, content: ov.content });
    delete data[d.code];
  }
  const next: ReportWorkspaceData = { ...base(ws), section_data: data };
  delete next.authoring_mode;
  if (kept.length > 0) next.legacy_sections = kept;
  return next;
}
